import { useState } from "react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Target, Trash2, Plus, Pencil } from "lucide-react";
import { GoalStrategies } from "./GoalStrategies";
import { GoalLoad } from "./GoalLoad";

export default function GoalsSection({ title, subtitle, goals, canGive, onAdd, onDelete, onEdit, testid, requiresParent = false, parentOptions = [], strategies, canLink, onLinkStrategies, memberSkills, onProgress, transferCtx, onLoad, canLoadTaker }) {
  const [text, setText] = useState("");
  const [parentId, setParentId] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editParent, setEditParent] = useState("");

  const noParents = requiresParent && (parentOptions || []).length === 0;

  const submit = async () => {
    if (!text.trim()) return;
    if (requiresParent && !parentId) {
      toast.error("Kies eerst een hoofddoel van Systeem V om aan te koppelen");
      return;
    }
    setBusy(true);
    try {
      await onAdd(text.trim(), parentId || null);
      setText("");
      setParentId("");
      setAdding(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon doel niet toevoegen");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await onDelete(id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon doel niet verwijderen");
    }
  };

  const startEdit = (g) => {
    setEditId(g.id);
    setEditText(g.text);
    setEditParent(g.parent_goal_id || "");
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    if (requiresParent && !editParent) {
      toast.error("Kies een hoofddoel om aan te koppelen");
      return;
    }
    setBusy(true);
    try {
      await onEdit(editId, editText.trim(), editParent || null);
      setEditId(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kon doel niet bijwerken");
    } finally {
      setBusy(false);
    }
  };

  const ParentSelect = ({ value, onChange }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-none border-zinc-400 h-9" data-testid="goal-parent-select">
        <SelectValue placeholder="Koppel aan hoofddoel (Systeem V)…" />
      </SelectTrigger>
      <SelectContent>
        {parentOptions.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            {p.text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="mt-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-2">
        <Target className="h-3.5 w-3.5" /> {title}
      </div>
      {subtitle && <p className="text-[11px] text-zinc-400 mb-2">{subtitle}</p>}
      <div className="space-y-2">
        {(goals || []).map((g) =>
          editId === g.id ? (
            <div key={g.id} className="border-l-2 border-zinc-900 bg-zinc-50 px-3 py-2 space-y-2" data-testid="goal-edit-form">
              {requiresParent && <ParentSelect value={editParent} onChange={setEditParent} />}
              <Input value={editText} onChange={(e) => setEditText(e.target.value)} className="rounded-none border-zinc-400 h-9" data-testid="goal-edit-input" onKeyDown={(e) => e.key === "Enter" && saveEdit()} autoFocus />
              <div className="flex gap-2">
                <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-8" size="sm" onClick={saveEdit} disabled={busy} data-testid="save-goal-edit-button">
                  Opslaan
                </Button>
                <Button variant="outline" className="rounded-none h-8" size="sm" onClick={() => setEditId(null)}>
                  Annuleren
                </Button>
              </div>
            </div>
          ) : (
            <div key={g.id} className="border-l-2 border-zinc-900 bg-zinc-50 px-3 py-2 flex items-start justify-between gap-2" data-testid="goal-item">
              <div className="min-w-0 flex-1">
                <div className="text-sm">{g.text}</div>
                {g.parent_goal_text && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">↳ hoofddoel: {g.parent_goal_text}</div>}
                {g.author && <div className="text-[10px] font-mono text-zinc-400">— {g.author}</div>}
                {onLoad && <GoalLoad goal={g} canGiver={canGive} canTaker={canLoadTaker} onSave={onLoad} />}
                {onLinkStrategies && <GoalStrategies goal={g} strategies={strategies || []} canLink={canLink} onLink={onLinkStrategies} memberSkills={memberSkills} onProgress={onProgress} transferCtx={transferCtx} />}
              </div>
              {canGive && (
                <div className="flex gap-1 shrink-0">
                  {onEdit && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEdit(g)} data-testid="edit-goal-button">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => handleDelete(g.id)} data-testid="delete-goal-button">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          )
        )}
        {(goals || []).length === 0 && <p className="text-sm text-zinc-400">Nog geen doelen.</p>}
      </div>

      {canGive &&
        (noParents ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-2" data-testid="no-main-goal-hint">
            Er is nog geen hoofddoel gesteld door Systeem V. Subdoelen kunnen pas worden toegevoegd zodra er een hoofddoel bestaat.
          </p>
        ) : adding ? (
          <div className="space-y-2 mt-2">
            {requiresParent && <ParentSelect value={parentId} onChange={setParentId} />}
            <div className="flex gap-2">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={requiresParent ? "Nieuw subdoel…" : "Nieuw hoofddoel…"}
                className="rounded-none border-zinc-400 h-9"
                data-testid="goal-input"
                onKeyDown={(e) => e.key === "Enter" && submit()}
                autoFocus
              />
              <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-9" onClick={submit} disabled={busy} data-testid="save-goal-button">
                OK
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="rounded-none w-full mt-2" onClick={() => setAdding(true)} data-testid="add-goal-button">
            <Plus className="h-3.5 w-3.5 mr-1" /> {requiresParent ? "Subdoel toevoegen" : "Hoofddoel toevoegen"}
          </Button>
        ))}
    </div>
  );
}
